import { History } from "history";
import { AccessType } from "@/types/enums/AccessType";
import { compact, get, trimStart } from "lodash";
import { TransactionType } from "@/types/enums/TransactionType";
import { ProjectNavigationState } from "@/dbot/project/projectDomain";
import { toQueryString } from "./queryParams";
import {
  OwnershipGraphType,
  ownershipGraphTypeToPath,
} from "@/overview/ownership/commonOwnershipDomain";
import { Language } from "@/i18n/languages";

import { SigningMethod } from "@/common/common";
import {
  extractPortfolioInstanceUrl,
  extractPortfolioUrl,
  SelectedPortfolioOption,
} from "@/portfolio/portfolioDomain";
import { DCEvent } from "@/events/eventsDomain";
import {
  ShareholderCommitmentType,
  ShareholderContributionType,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { ShareholderDetailsTab } from "@/shareholderRegister/shareholders/shareholderDetailsTypes";
import { PartnerDetailsTab } from "@/partnershipRegister/partnershipRegisterTypes";
import { ArchiveRootFolderType } from "@/archive/commonArchiveDomain";
import { NoticeActiveView } from "@/meetings/generalMeeting/generalMeetingDomain";
import { PowerOfAttorneyTab } from "@/overview/powerOfAttorney/powerOfAttorneyDomain";
import { isProductionEnvironment } from "./environments";
import { PortfolioFeatureToggles } from "@/common/featureToggles";
import { CustomRoleTab } from "@/overview/companySettings/companySettingsDomain.ts";
import { UserSettingsTab } from "@/userSettings/userSettingsDomain.ts";

// Navigation state

export type NavigationState = {
  type: "navigation-state";
  referrer?: string;
};

export const navigationState = ({ referrer }: { referrer: string }): NavigationState => ({
  type: "navigation-state",
  referrer,
});

export const isNavigationState = (o: unknown): o is NavigationState =>
  get(o, "type") === "navigation-state";

export const navigationReferrerUrl = (state: unknown): string | undefined =>
  isNavigationState(state) ? state.referrer : undefined;

//  Shareholder links
export const shareholderBase = (orgNo: string, shareholderId: number): string =>
  `/shareholder/${orgNo}/${shareholderId}`;

//Overview
export const shareholderOverview = (
  orgNo: string,
  shareholderId: number,
  tab?: ShareholderDetailsTab | "dashboard",
): string => shareholderBase(orgNo, shareholderId) + (tab ? `/${tab}` : "");

export const shareholderOverviewArticles = (orgNo: string, shareholderId: number): string =>
  shareholderBase(orgNo, shareholderId) + `/articlesofassociation/`;

//Shareholder archive
export const shareholderArchive = (orgNo: string, shareholderId: number): string =>
  shareholderBase(orgNo, shareholderId) + "/archive";

//Shareholder messaging
export const shareholderMessaging = (
  orgNo: string,
  shareholderId: number,
  messageId?: string,
): string => compact([shareholderBase(orgNo, shareholderId), "messaging", messageId]).join("/");

// ------------------------------------------------------
//Board member links
export const boardMemberBase = (orgNo: string): string => `/board-member/${orgNo}`;
export const boardMemberOverview = (orgNo: string): string => boardMemberBase(orgNo);
export const boardMemberOverviewArticles = (orgNo: string): string =>
  boardMemberOverview(orgNo) + "/articlesofassociation";
export const boardMemberMessaging = (orgNo: string): string =>
  boardMemberBase(orgNo) + "/messaging";
export const boardMemberArchive = ({ orgNo, folderId }: { orgNo: string; folderId?: string }) =>
  `${boardMemberBase(orgNo)}/archive${toQueryString({ folderId })}`;

export const boardMemberMeetings = (orgNo: string): string => boardMemberBase(orgNo) + "/meetings";
export const boardMemberSchedule = (orgNo: string): string => boardMemberBase(orgNo) + "/schedule";

export const boardMemberBoardMeetings = (
  orgNumber: string,
  projectId: string,
  view?: "notice" | "minutes",
) => {
  switch (view) {
    case "notice":
      return `${boardMemberMeetings(orgNumber)}/${projectId}/board-meeting/notice`;
    case "minutes":
      return `${boardMemberMeetings(orgNumber)}/${projectId}/board-meeting/minutes`;
    default:
      return `${boardMemberMeetings(orgNumber)}/${projectId}/board-meeting`;
  }
};

export const boardMemberBoardMeetingPage = (orgNumber: string, projectId: string) => {
  return `${boardMemberMeetings(orgNumber)}/project/${projectId}`;
};

export const noticeForBoardMember = (noticeId: string) => {
  return `/notice-board-meeting/${noticeId}`;
};

export const matterViewPage = (meetingEmailId: string, matterId: string) => {
  return `/board-minutes-review/${meetingEmailId}/matter/${matterId}`;
};

export const previewBoardMeetingForAdmin = (orgNo: string, projectId: string) =>
  adminBase(orgNo) + "/board-meeting-preview/" + projectId;

export const previewGeneralMeetingForAdmin = (orgNo: string, projectId: string) =>
  adminBase(orgNo) + "/general-meeting-preview/" + projectId;

// ------------------------------------------------------

// Admin links
export const adminBase = (orgNumber: string) => `/company/${orgNumber}`;

//Overview
export const adminOverview = (orgNumber: string) => adminBase(orgNumber);

export const adminDashboard = (orgNumber: string) => adminOverview(orgNumber) + "/dashboard";

export const adminEntityData = (orgNumber: string) => adminOverview(orgNumber) + "/entity-data";

export const adminCompanyDashboard = (orgNumber: string, dashboardId: string) =>
  `${adminOverview(orgNumber)}/dashboards/${encodeURIComponent(dashboardId)}`;

export const adminArticles = (orgNumber: string) =>
  adminOverview(orgNumber) + "/articlesofassociation";

export const adminPoA = (orgNumber: string) => adminOverview(orgNumber) + "/power-of-attorney";

export const adminPoA2 = (orgNo: string, powerOfAttorneyTab: PowerOfAttorneyTab) =>
  adminOverview(orgNo) + `/power-of-attorney/${powerOfAttorneyTab}`;

export const userSettingsTabUrl = (tab: UserSettingsTab, referrer?: string) =>
  `/user/settings/${tab}${toQueryString({ referrer })}`;

export const userSettingsUrl = (referrer?: string) => userSettingsTabUrl("account", referrer);

export const adminPoADetails = (orgNo: string, powerOfAttorneyId: string) =>
  adminOverview(orgNo) + `/power-of-attorney/${powerOfAttorneyId}/details`;

export const adminOwnership = (orgNumber: string) => adminOverview(orgNumber) + "/ownership";

export const adminOwnershipMap = ({
  orgNo,
  graphType,
  selectedNodeId,
}: {
  orgNo: string;
  graphType: OwnershipGraphType;
  selectedNodeId?: string;
}) =>
  `${adminOwnership(orgNo)}/${ownershipGraphTypeToPath(graphType)}/map${toQueryString({ selectedNodeId })}`;

export type CompanyRolesTab = "current" | "former" | "issues";

export const adminRoles = (orgNumber: string, tab?: CompanyRolesTab) =>
  adminOverview(orgNumber) + "/roles" + (tab ? `/${tab}` : "");

export const toCompanyRolesTab = (s?: string): CompanyRolesTab =>
  s === "issues" || s === "former" ? s : "current";

export const adminRolesDetails = (
  orgNumber: string,
  companyRoleId: number,
  tab?: CompanyRolesTab,
) => adminOverview(orgNumber) + "/role" + `/${companyRoleId}/details` + toQueryString({ tab });

export const adminSettings = (orgNumber: string) => adminOverview(orgNumber) + "/settings";

export const adminUsersSettings = (orgNumber: string) =>
  adminOverview(orgNumber) + "/settings/users";

export const adminPermissionsSettings = (orgNumber: string) =>
  adminOverview(orgNumber) + "/settings/permissions";

export const adminSettingsShareholders = (orgNo: string) =>
  adminBase(orgNo) + "/settings/shareholders";

export const adminSettingsBilling = (orgNo: string) => adminBase(orgNo) + "/settings/billing";

// Partners
export const adminPartners = (orgNumber: string) => adminBase(orgNumber) + "/partners";
export const adminPartnersPerDate = (orgNumber: string) => adminPartners(orgNumber) + "/per-date";
export const adminPartnersTransactions = (orgNumber: string) =>
  adminPartners(orgNumber) + "/transactions";
export const adminPartnersTransaction = (orgNumber: string, transactionId: string) =>
  adminPartnersTransactions(orgNumber) + "/" + transactionId;
export const adminPartnersTransactionEdit = (orgNumber: string, transactionId: string) =>
  adminPartnersTransaction(orgNumber, transactionId) + "/edit";
export const adminPartnerDetails = (
  orgNumber: string,
  partnershipInterestId: string,
  tab?: PartnerDetailsTab,
) => adminPartners(orgNumber) + `/${partnershipInterestId}` + (tab ? `/${tab}` : "");

// Shareholders
export const adminShareholders = (orgNumber: string) => adminBase(orgNumber) + "/shareholders";
export const adminShareholdersDashboard = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/dashboard";
export const adminShareholdersList = (orgNumber: string) => adminShareholders(orgNumber) + "/list";
export const adminEditShareholders = (orgNumber: string, incomplete?: boolean) =>
  adminShareholders(orgNumber) + `/edit${incomplete ? toQueryString({ incomplete }) : ""}`;
export const adminShareholdersImport = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/import";

// Signatures

export const adminFollowUp = (orgNumber: string) => adminBase(orgNumber) + "/follow-up";
export const adminFollowUpMatters = (orgNumber: string) =>
  adminBase(orgNumber) + "/follow-up/matters";
export const adminFollowUpSignatures = (orgNumber: string) =>
  adminBase(orgNumber) + "/follow-up/signatures";
export const adminFollowUpCompanyInfo = (orgNumber: string) =>
  adminBase(orgNumber) + "/follow-up/company-info";
export const adminFollowUpShareholders = (orgNumber: string) =>
  adminBase(orgNumber) + "/follow-up/shareholders";

export const adminShareholderDetails = (
  orgNumber: string,
  shareholderId: number,
  tab?: ShareholderDetailsTab,
) => adminShareholders(orgNumber) + `/list/${shareholderId}` + (tab ? `/${tab}` : "");

export const adminShareholdersTransactions = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/transactions";
export const adminShareholdersTransaction = (orgNumber: string, transactionLogId: number) =>
  `${adminShareholders(orgNumber)}/transactions/${transactionLogId}`;
export const adminEditShareholdersTransaction = (orgNumber: string, transactionLogId: number) =>
  `${adminShareholders(orgNumber)}/transactions/${transactionLogId}/edit`;
export const adminFinancialInstruments = (orgNumber: string) =>
  adminBase(orgNumber) + "/instruments";
export const adminShareholdersCapTable = (orgNumber: string) =>
  `${adminFinancialInstruments(orgNumber)}/cap-table`;

export const adminCapTableScenario = (orgNumber: string, scenarioId: string) =>
  `${adminShareholdersCapTable(orgNumber)}/scenarios/${encodeURIComponent(scenarioId)}`;
export const adminShareholdersOptions = (orgNumber: string) =>
  `${adminFinancialInstruments(orgNumber)}/options`;
export const adminOptionHolders = (orgNumber: string) =>
  `${adminShareholdersOptions(orgNumber)}/holders`;
export const adminOptionPrograms = (orgNumber: string) =>
  `${adminShareholdersOptions(orgNumber)}/programs`;
export const adminOptionProgram = (orgNumber: string, planId: string) =>
  `${adminOptionPrograms(orgNumber)}/${encodeURIComponent(planId)}`;
export const adminOptionExercises = (orgNumber: string) =>
  `${adminShareholdersOptions(orgNumber)}/exercises`;

export const adminShareholdersShareClasses = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/classes";
export const adminShareholdersShareClass = (orgNumber: string, shareTypeId: number) =>
  `${adminShareholders(orgNumber)}/classes/${shareTypeId}`;
export const adminShareholdersCreateShareClass = (orgNumber: string) =>
  `${adminShareholders(orgNumber)}/classes/create`;

export const adminShareholdersShareholderCommitments = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/commitments";
export const adminShareholdersShareholderCommitmentLog = (orgNumber: string) =>
  adminShareholdersShareholderCommitments(orgNumber) + "/log";
export const adminShareholdersShareholderCommitmentDetails = (
  orgNumber: string,
  commitmentId: string,
) => `${adminShareholdersShareholderCommitmentLog(orgNumber)}/${commitmentId}`;
export const adminShareholdersNewShareholderCommitment = (
  orgNumber: string,
  commitmentType: ShareholderCommitmentType,
) => `${adminShareholdersShareholderCommitments(orgNumber)}/new/${commitmentType.toLowerCase()}`;

export const adminShareholdersShareholderContributions = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/contributions";
export const adminShareholdersShareholderContributionLog = (orgNumber: string) =>
  adminShareholdersShareholderContributions(orgNumber) + "/log";
export const adminShareholdersShareholderContributionDetails = (
  orgNumber: string,
  contributionId: number,
) => `${adminShareholdersShareholderContributionLog(orgNumber)}/${contributionId}`;
export const adminShareholdersNewShareholderContribution = (
  orgNumber: string,
  contributionType: ShareholderContributionType,
) => `${adminShareholders(orgNumber)}/contributions/new/${contributionType.toLowerCase()}`;

export const adminShareholderStatement = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/statement";
export const adminShareholderStatementNew = (orgNumber: string, year: number) =>
  adminShareholders(orgNumber) + `/statement/new/${year}`;
export const adminShareholderStatementId = (orgNumber: string, id: string) =>
  adminShareholders(orgNumber) + `/statement/${id}`;
export const adminBeneficialOwners = (orgNumber: string) =>
  adminShareholders(orgNumber) + "/beneficial-owners";
export const adminNewBeneficialOwnersStatement = (orgNumber: string) =>
  `${adminBeneficialOwners(orgNumber)}/new`;
export const adminBeneficialOwner = (orgNumber: string, beneficialOwnerId: string) =>
  `${adminBeneficialOwners(orgNumber)}/${beneficialOwnerId}`;
export const adminBeneficialOwnerLogDetails = (orgNumber: string, statementId: string) =>
  `${adminBeneficialOwners(orgNumber)}/log/${statementId}`;
export const adminBeneficialOwnerLogOwnerDetails = (
  orgNumber: string,
  statementId: string,
  beneficialOwnerId: string,
) => `${adminBeneficialOwners(orgNumber)}/log/${statementId}/${beneficialOwnerId}`;
//Dbot
export const adminProjects = (orgNumber: string) => adminBase(orgNumber) + "/projects";
export const dbot = (orgNumber: string) => adminBase(orgNumber) + "/dbot";

export type ProjectRouteParams = {
  orgNo: string;
  projectId: string;
};

export const adminProject = ({ orgNo, projectId }: ProjectRouteParams) =>
  `${adminBase(orgNo)}/projects/${projectId}`;

export const adminPermissionsSettingsWithTab = (orgNo: string, tab: CustomRoleTab) =>
  adminBase(orgNo) + `/settings/permissions/${tab}`;

// Schedule
export type ScheduleRouteParams = {
  accessType: AccessType;
  orgNo: string;
  view?: string;
  year?: string;
  month?: string;
  selectedItem?: string;
  activeTab?: string;
  shareholderId?: number;
};

export const scheduleUrl = ({
  accessType,
  activeTab,
  month,
  orgNo,
  selectedItem,
  shareholderId,
  view,
  year,
}: ScheduleRouteParams) => {
  const path = (): (string | number)[] => {
    switch (true) {
      case view === undefined:
        return [];
      case year === undefined:
        return [view];
      case month === undefined:
        return [view, year];
      default:
        return [view, year, month];
    }
  };
  return (
    [
      accessType === AccessType.BOARD_MEMBER
        ? boardMemberBase(orgNo)
        : accessType === AccessType.SHAREHOLDER ||
            accessType === AccessType.ADDITIONAL_REPRESENTATIVE
          ? shareholderBase(orgNo, shareholderId ?? 0)
          : adminBase(orgNo),
      "schedule",
      ...path(),
    ].join("/") + toQueryString({ selectedItem, activeTab })
  );
};

//company settings
export type CompanySettingsRouteParams = {
  menuItem?: string;
  roleTab?: CustomRoleTab;
};

//Archive
export const adminArchive = ({
  orgNo,
  folderId,
  folderType,
  view,
}: {
  orgNo: string;
  folderId?: string;
  folderType?: string;
  view?: string;
}) =>
  compact([`${adminBase(orgNo)}/archive`, folderType, view]).join("/") +
  toQueryString({ folderId });

export const documentLocation = ({
  orgNo,
  parentTitle,
  archiveRootFolder,
  folderType,
  folderId,
}: {
  orgNo: string;
  parentTitle: string;
  archiveRootFolder?: string;
  folderType?: string;
  folderId?: string;
}): string => {
  const folderQuery = toQueryString({ folderId });
  if (folderType === ArchiveRootFolderType.BOARD_ARCHIVE) {
    return `${adminBoardArchive(orgNo)}${folderQuery}`;
  } else if (folderType === ArchiveRootFolderType.SHAREHOLDER_ARCHIVE) {
    return `${adminShareholderArchive(orgNo)}${folderQuery}`;
  } else if (
    folderType === ArchiveRootFolderType.ADMIN_ARCHIVE ||
    archiveRootFolder === ArchiveRootFolderType.ADMIN_ARCHIVE
  ) {
    return `${adminBase(orgNo)}/archive/admin-archive${folderQuery}`;
  }
  switch (parentTitle) {
    case "board_archive_folder":
      return `${adminBoardArchive(orgNo)}${folderQuery}`;
    case "shareholder_archive_folder":
      return `${adminShareholderArchive(orgNo)}${folderQuery}`;
    case "admin_archive_folder":
      return `${adminBase(orgNo)}/archive/admin-archive${folderQuery}`;
    default:
      return `${adminBase(orgNo)}/archive/${archiveRootFolder}${folderType ? "/" + folderType : ""}${folderQuery}`;
  }
};

export const adminBoardArchive = (orgNumber: string) =>
  adminBase(orgNumber) + "/archive/board-archive";

export const adminShareholderArchive = (orgNumber: string) =>
  adminBase(orgNumber) + "/archive/shareholder-archive";

//Messaging
export const adminMessaging = (orgNumber: string) => adminBase(orgNumber) + "/messaging";

//navigation functions
export const navigateToShareholderContributions = (orgNo: string, history: History) =>
  history.push(adminShareholdersShareholderContributions(orgNo));

export const navigateToShareholderContributionDetails = (
  orgNo: string,
  contributionId: number,
  history: History,
) => history.push(adminShareholdersShareholderContributionDetails(orgNo, contributionId));

export const navigateToNewShareholderContribution = (
  orgNo: string,
  history: History,
  contributionType: ShareholderContributionType,
) => history.push(adminShareholdersNewShareholderContribution(orgNo, contributionType));

export const navigateToShareholderCommitments = (orgNo: string, history: History) =>
  history.push(adminShareholdersShareholderCommitments(orgNo));

export const navigateToShareholderCommitmentDetails = (
  orgNo: string,
  commitmentId: string,
  history: History,
) => history.push(adminShareholdersShareholderCommitmentDetails(orgNo, commitmentId));

export const navigateToNewShareholderCommitment = (
  orgNo: string,
  history: History,
  commitmentType: ShareholderCommitmentType,
) => history.push(adminShareholdersNewShareholderCommitment(orgNo, commitmentType));

export const navigateToAdminDashboard = (organisationNumber: string, history: History) =>
  history.push(adminDashboard(organisationNumber));

export const navigateToPoADetails = (orgNo: string, powerOfAttorneyId: string): DCEvent => ({
  type: "navigate",
  url: adminPoADetails(orgNo, powerOfAttorneyId),
});

export const navigateToShareType = (orgNo: string, shareTypeId: number, history: History) =>
  history.push(adminShareholdersShareClass(orgNo, shareTypeId));

export const navigateToCreateShareType = (orgNo: string, history: History) =>
  history.push(adminShareholdersCreateShareClass(orgNo));

export const navigateToTransaction = (
  orgNo: string,
  transactionLogId: number,
  history: History,
  queryString?: string,
) => history.push(adminShareholdersTransaction(orgNo, transactionLogId) + (queryString ?? ""));

export const navigateToProject = ({
  orgNo,
  projectId,
  history,
  state,
}: {
  orgNo: string;
  projectId: string;
  history: History;
  state?: ProjectNavigationState;
}) => history.push(adminProject({ orgNo, projectId }), state);

export const navigateToProjectEvent = (
  routeParams: ProjectRouteParams,
  state?: ProjectNavigationState,
): DCEvent => ({
  type: "navigate",
  url: adminProject(routeParams),
  state,
});

export const navigateToProjectOverview = (organisationNumber: string, history: History) =>
  history.push({ pathname: adminProjects(organisationNumber) });

export const navigateToShareholderDetail = (
  orgNo: string,
  shareholderId: number,
  history: History,
  queryString?: string,
  tab?: ShareholderDetailsTab,
  referrer?: string,
) =>
  history.push(
    adminShareholderDetails(orgNo, shareholderId, tab) + (queryString ?? ""),
    referrer === undefined ? undefined : navigationState({ referrer }),
  );

export const navigateToNewTransaction = (
  orgNo: string,
  history: History,
  transactionType: TransactionType,
) => history.push(adminShareholders(orgNo) + `/transactions/new/${transactionType.toLowerCase()}`);

export const navigateToShareholderStatement = (orgNo: string, history: History) =>
  history.push(adminShareholderStatement(orgNo));

export const navigateToShareholdersList = (orgNo: string, history: History) =>
  history.push(adminShareholdersList(orgNo));

export const navigateToEditShareholders = (orgNo: string, history: History, incomplete?: boolean) =>
  history.push(adminEditShareholders(orgNo, incomplete));

export const navigateToShareholdersImport = (orgNo: string, history: History) =>
  history.push(adminShareholdersImport(orgNo));

export const navigateToTransactions = (orgNo: string, history: History, queryString?: string) =>
  history.push(adminShareholdersTransactions(orgNo) + (queryString ?? ""));

export const navigateToProjectList = (orgNo: string, history: History) =>
  history.push(adminProjects(orgNo));

export const resolveNoticeForShareholderUrl = (documentId: string): string =>
  `/notice-general-meeting/resolve/${documentId}`;

export const noticeForShareholderUrl = (
  noticeForShareholderId: string,
  view?: NoticeActiveView,
): string => `/notice-general-meeting/${noticeForShareholderId}` + (view ? `/${view}` : "");

export const noticePoaRecipientUrl = (poaRecipientId: string): string =>
  `/notice-general-meeting-poa-recipient/${poaRecipientId}`;

export const navigateGeneralMeetingPreview = (orgNo: string, projectId: string) => {
  openLinkInNewWindow(`/company/${orgNo}/general-meeting-preview/${projectId}`);
};

export const portfolioUrl = "/portfolio";

export const navigateToPortfolio = (history: History) => {
  history.push(portfolioUrl);
};

export const portfolioCompaniesUrl = `${portfolioUrl}/all/companies`;

export const portfolioSearchUrl = `${portfolioUrl}/search`;

export const portfolioReportsUrl = `${portfolioUrl}/reports`;

export const defaultPortfolioUrl = (portfolioFeatures: PortfolioFeatureToggles) =>
  portfolioFeatures.search ? portfolioSearchUrl : portfolioCompaniesUrl;

export const navigateToUserPortfolio = (id: string, history: History) =>
  history.push(extractPortfolioInstanceUrl(id) + "/companies");

export const navigateToAdminCompanyPortfolios = (
  portfolio: SelectedPortfolioOption,
  history: History,
) => {
  history.push(extractPortfolioUrl(portfolio));
};
export const navigateToBatchSigningPage = (history: History, signingMethod: SigningMethod) => {
  history.push(`/signing/${fromSigningMethodToParameter(signingMethod)}`);
};

export const navigateBackWithFallback = (history: History, location: string | null) => {
  if (location) {
    history.push(location);
  } else {
    navigateToPortfolio(history);
  }
};

export const companyUrl = (
  orgNo: string,
  accessType: AccessType,
  shareholderId?: number,
): string =>
  accessType === AccessType.BOARD_MEMBER
    ? boardMemberSchedule(orgNo)
    : (accessType === AccessType.SHAREHOLDER ||
          accessType === AccessType.ADDITIONAL_REPRESENTATIVE) &&
        shareholderId
      ? shareholderOverview(orgNo, shareholderId)
      : adminOverview(orgNo);

export function enterpriseAccountUrl(id: string) {
  return `/enterprise-account/${id}`;
}

export const navigateToRoot = (history: History) => history.push("/");

export const loginUrl = () => "/login";
export const navigateToLogin = (history: History) => history.push(loginUrl());

export const superadminUrl = "/super-admin-backoffice";

export const superadminSearchUrl = (orgNo: string) => `${superadminUrl}/search/${orgNo}`;
export const superadminUserUrl = (userId: string | number) =>
  `${superadminUrl}/search-user/${userId}`;

export const customRoleCompanyUrl = ({ baseUrl, roleId }: { baseUrl: string; roleId: string }) =>
  `${baseUrl}?roleId=${roleId}`;

export const openLinkInNewWindow = (url: string) => {
  window.open(url, "_blank");
};

export const getReturnToAfterPaymentUrl = (
  organisationNumber: string,
  returnToLocation?: string,
) => {
  const shareholderBaseUrl = `${window.location.origin}${adminShareholders(organisationNumber)}`;
  if ("transactions" === returnToLocation) {
    return `${shareholderBaseUrl}/transactions/new`;
  } else if ("altinn" === returnToLocation) {
    return `${shareholderBaseUrl}/altinn`;
  } else if (returnToLocation !== undefined) {
    return decodeURI(returnToLocation);
  } else {
    return window.location.href;
  }
};

export const applicationFrontPageUrl = () => "https://app.dcompany.no/";
export const navigateToNorwegianBankIdLogin = () => (location.href = "/api/auth/bankid");
export const navigateToFrontpage = () => (location.href = "https://app.dcompany.no/");
export const dcompanyFrontpageUrl = (lang: Language) => {
  switch (lang) {
    case Language.NO:
      return "https://dcompany.no";
    default:
      return "https://dcompany.no/en";
  }
};
export const giveUsFeedbackUrl = "https://airtable.com/apppdnKJrnENisz3z/shrKU5vSljC5QZs3j";
export const giveUsFeedback = () => openLinkInNewWindow(giveUsFeedbackUrl);

export const joinUrl = (baseUrl: string, path: string) =>
  new URL(baseUrl).origin + "/" + trimStart(path, "/");

export function altinnManageAccessUrl() {
  return isProductionEnvironment()
    ? "https://am.ui.altinn.no/accessmanagement/ui/systemuser/overview"
    : "https://am.ui.tt02.altinn.no/accessmanagement/ui/systemuser/overview";
}

export const downloadShareholdersExcelImportTemplate = () =>
  (window.location.href = "/api/shareholders/download-excel-template");

export const downloadOptionsExcelImportTemplate = () =>
  (window.location.href = "/api/instruments/download-excel-template");

export const downloadPrefilledOptionsExcelImportTemplate = (orgNo: string) => () =>
  (window.location.href = `/api/instruments/${orgNo}/download-prefilled-excel-template`);

export const downloadRegisteredOptionHoldersExcel = (orgNo: string) => () =>
  (window.location.href = `/api/instruments/${orgNo}/download-option-holders-excel`);

export function fromParametertoSigningMethod(parameter: string) {
  switch (parameter) {
    case "bankid":
      return SigningMethod.BANKID;
    case "bankidno":
      return SigningMethod.BANKID_AUTH;
    case "bankidse":
      return SigningMethod.BANKID_SWEDEN_AUTH;
    case "handwriting":
      return SigningMethod.HANDWRITING;
    case "email":
      return SigningMethod.AUTH_EMAIL;
    default:
      return SigningMethod.BANKID_AUTH;
  }
}

export function fromSigningMethodToParameter(signingMethod: SigningMethod) {
  switch (signingMethod) {
    case SigningMethod.BANKID:
      return "bankid";
    case SigningMethod.BANKID_AUTH:
      return "bankidno";
    case SigningMethod.BANKID_SWEDEN_AUTH:
      return "bankidse";
    case SigningMethod.HANDWRITING:
      return "handwriting";
    case SigningMethod.AUTH_EMAIL:
      return "email";
    default:
      return "bankidno";
  }
}
